import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import Dropzone from 'react-dropzone';
import styles from '../../badging.module.scss';
import PulpoField from '../../../../pulpo_visualizer/fields';
import CustomFields from '../../../../components/customFields/CustomFields';
import Clickable from '../../../../components/clickable/Clickable';
import Collapsible from '../../../../components/collapsible/Collapsible';
import AttachmentsList from './AttachmentsList';

const Step0 = ({
  info,
  handleFieldErrorChanged,
  showFormErrors,
  handleAnswerChanged,
  requiredMap,
  fields,
  response,
  handleRemoveImageA,
  handleRemoveImageB,
  handleRemoveImageC,
  fieldErrors,
  airportCode,
  pdfUrl,
  handleDropList,
  requiredAttachments,
  handleDropListB,
  handleDropListC,
  handleRemoveImage,
  listAValues,
  listBValues,
  listCValues,
  handleDropListA,
  requiredAttachmentsErrors,
  handleAddmore,
  pdfHost,
  schemaSettings,
  listAMessage,
  listBMessage,
  listCMessage,
  applicantAcknowldgementMessage,
  tenantName,
  renewalApplicationData
}) => {
  const orderFieldsBySettings = (list, ordering) => {
    if (!Array.isArray(list)) return [];
    if (!Array.isArray(ordering) || ordering.length === 0) return list;

    const byId = new Map(list.map(field => [field.id, field]));
    const ordered = [];
    ordering.forEach((id) => {
      if (byId.has(id)) {
        ordered.push(byId.get(id));
        byId.delete(id);
      }
    });

    list.forEach((field) => {
      if (byId.has(field.id)) {
        ordered.push(field);
        byId.delete(field.id);
      }
    });

    return ordered;
  };

  const ordering = schemaSettings?.ordering
    || schemaSettings?.fields_ordering
    || schemaSettings?.fields_order;
  const orderedFields = orderFieldsBySettings(fields, ordering);
  const hasCustomFields = Object.keys(requiredMap).length > 0 && Array.isArray(orderedFields) && orderedFields.length > 0;
  const attachmentFieldIds = new Set(['attachmentslist_a', 'attachmentslist_b', 'attachmentslist_c']);
  const identificationFieldIds = new Set([
    'social_security_number',
    'driver_state',
    'drivers_license_number',
    'passport',
    'passport_country',
    'alien_registration_number'
  ]);
  const usOnlyIdentificationFieldIds = new Set([
    'social_security_number',
    'driver_state',
    'drivers_license_number'
  ]);

  const citizenshipValue = response?.nation_of_citizenship?.value;
  const showIdentificationFields = Boolean(citizenshipValue);
  const isUSCitizen = citizenshipValue === 'US';

  const shouldRenderField = (field) => {
    if (identificationFieldIds.has(field?.id)) {
      if (!showIdentificationFields) return false;
      if (isUSCitizen) return usOnlyIdentificationFieldIds.has(field?.id);
    }
    return true;
  };

  const visibleOrderedFields = orderedFields.filter(field => field && shouldRenderField(field));

  const orderedBlocks = [];
  let currentChunk = [];
  visibleOrderedFields.forEach((field) => {
    if (attachmentFieldIds.has(field?.id)) {
      if (currentChunk.length > 0) {
        orderedBlocks.push({ type: 'custom', fields: currentChunk });
        currentChunk = [];
      }
      orderedBlocks.push({ type: 'attachment', field });
      return;
    }
    currentChunk.push(field);
  });
  if (currentChunk.length > 0) {
    orderedBlocks.push({ type: 'custom', fields: currentChunk });
  }

  const renderAttachmentField = (fil) => {
    if (!fil || fil?.isDisabled) return null;
    if (fil.id === 'attachmentslist_a') {
      return (
        <>
          <div className={styles.fullInput}>
            <div className={styles.attachmentsCard}>
              {fil.settings?.add_req_docs
                ? <AttachmentsList
                    requiredAttachments={requiredAttachments}
                    handleAnswerChanged={handleAnswerChanged}
                    handleRemoveImage={handleRemoveImage}
                    handleDropList={handleDropList}
                    handleAddmore={handleAddmore}
                    list="attachments_listA"
                    requiredAttachmentsErrors={requiredAttachmentsErrors}
                    showFormErrors={showFormErrors}
                    dropDownValues={listAValues}
                    field={fil}
                    fields={orderedFields}
                  />
                : <Dropzone onDrop={handleDropListA} disableClick
                    className={styles.dropzone}
                  >
                    {({ open }) => (
                      <>
                        <p className={styles.browsetext}>
                          <FormattedMessage id="airport.logo.text1" defaultMessage="Drag an image here or" />
                          <button type="button" onClick={() => open()}>
                            <FormattedMessage id="airport.logo.button" defaultMessage="browse" />
                          </button>
                          <FormattedMessage id="airport.logo.text2" defaultMessage="for an image to upload." />
                        </p>
                      </>
                    )}
                  </Dropzone>
              }
            </div>
            {!fil.settings?.add_req_docs && listAMessage && listAMessage.length > 0 && (
              // eslint-disable-next-line react/no-danger
              <div className={styles.checklist} dangerouslySetInnerHTML={{ __html: listAMessage }} />
            )}
          </div>
          {!fil.settings?.add_req_docs && info?.attachments_listA && info?.attachments_listA.length > 0 && (
            <div className={styles.uploadphotos}>
              {info?.attachments_listA.map(e => (
                <div key={e.name} className={styles.wrapper}>
                  <Clickable onClick={() => handleRemoveImageA(e)}>&times;</Clickable>
                  {e.type === 'application/pdf'
                    ? <p>{e.name.substring(0, 40) || e.file_name.substring(0, 40)}</p>
                    : <img src={e.preview || e.url} alt={e.name || e.file_name} />
                  }
                </div>
              ))}
            </div>
          )}
          {!fil.settings?.add_req_docs && fieldErrors.attachmentslist_a && showFormErrors && (
            <div className={styles.error}>
              <FormattedMessage id="pulpoforms.errors.not_blank" defaultMessage="This field cannot be empty" />
            </div>)
          }
        </>
      );
    }
    if (fil.id === 'attachmentslist_b') {
      return (
        <>
          <div className={styles.fullInput}>
            <div className={styles.attachmentsCard}>
              {fil.settings?.add_req_docs
                ? <AttachmentsList
                    requiredAttachments={requiredAttachments}
                    handleAnswerChanged={handleAnswerChanged}
                    handleRemoveImage={handleRemoveImage}
                    handleDropList={handleDropList}
                    handleAddmore={handleAddmore}
                    list="attachments_listB"
                    requiredAttachmentsErrors={requiredAttachmentsErrors}
                    showFormErrors={showFormErrors}
                    dropDownValues={listBValues}
                    field={fil}
                    fields={orderedFields}
                  />
                : <Dropzone
                    onDrop={handleDropListB}
                    disableClick
                    className={styles.dropzone}
                  >
                    {({ open }) => (
                      <>
                        <p className={styles.browsetext}>
                          <FormattedMessage
                            id="airport.logo.text1"
                            defaultMessage="Drag an image here or"
                          />
                          <button type="button" onClick={() => open()}>
                            <FormattedMessage
                              id="airport.logo.button"
                              defaultMessage="browse"
                            />
                          </button>
                          <FormattedMessage
                            id="airport.logo.text2"
                            defaultMessage="for an image to upload."
                          />
                        </p>
                      </>
                    )}
                  </Dropzone>}
            </div>
            {!fil.settings?.add_req_docs && listBMessage && listBMessage.length > 0 && (
              // eslint-disable-next-line react/no-danger
              <div className={styles.checklist} dangerouslySetInnerHTML={{ __html: listBMessage }} />
            )}
          </div>
          {!fil.settings?.add_req_docs && info?.attachments_listB && info?.attachments_listB.length > 0 && (
            <div className={styles.uploadphotos}>
              {info?.attachments_listB.map(e => (
                <div key={e.name} className={styles.wrapper}>
                  <Clickable onClick={() => handleRemoveImageB(e)}>&times;</Clickable>
                  {e.type === 'application/pdf'
                    ? <p>{e.name.substring(0, 40) || e.file_name.substring(0, 40)}</p>
                    : <img src={e.preview || e.url} alt={e.name || e.file_name} />
                  }
                </div>
              ))}
            </div>
          )}
          {!fil.settings?.add_req_docs && fieldErrors.attachmentslist_b && showFormErrors && (
            <div className={styles.error}>
              <FormattedMessage id="pulpoforms.errors.not_blank" defaultMessage="This field cannot be empty" />
            </div>)
          }
        </>
      );
    }
    if (fil.id === 'attachmentslist_c') {
      return (
        <>
          <div className={styles.fullInput}>
            <div className={styles.attachmentsCard}>
              {fil.settings?.add_req_docs
                ? <AttachmentsList
                    requiredAttachments={requiredAttachments}
                    handleAnswerChanged={handleAnswerChanged}
                    handleRemoveImage={handleRemoveImage}
                    handleDropList={handleDropList}
                    handleAddmore={handleAddmore}
                    list="attachments_listC"
                    requiredAttachmentsErrors={requiredAttachmentsErrors}
                    showFormErrors={showFormErrors}
                    dropDownValues={listCValues}
                    field={fil}
                    fields={orderedFields}
                  />
                : <Dropzone
                    onDrop={handleDropListC}
                    disableClick
                    className={styles.dropzone}
                  >
                    {({ open }) => (
                      <>
                        <p className={styles.browsetext}>
                          <FormattedMessage
                            id="airport.logo.text1"
                            defaultMessage="Drag an image here or"
                          />
                          <button type="button" onClick={() => open()}>
                            <FormattedMessage
                              id="airport.logo.button"
                              defaultMessage="browse"
                            />
                          </button>
                          <FormattedMessage
                            id="airport.logo.text2"
                            defaultMessage="for an image to upload."
                          />
                        </p>
                      </>
                    )}
                  </Dropzone>}
            </div>
            {!fil.settings?.add_req_docs && listCMessage && listCMessage.length > 0 && (
              // eslint-disable-next-line react/no-danger
              <div className={styles.checklist} dangerouslySetInnerHTML={{ __html: listCMessage }} />
            )}
          </div>
          {!fil.settings?.add_req_docs && info?.attachments_listC && info?.attachments_listC.length > 0 && (
            <div className={styles.uploadphotos}>
              {info?.attachments_listC.map(e => (
                <div key={e.name} className={styles.wrapper}>
                  <Clickable onClick={() => handleRemoveImageC(e)}>&times;</Clickable>
                  {e.type === 'application/pdf'
                    ? <p>{e.name.substring(0, 40) || e.file_name.substring(0, 40)}</p>
                    : <img src={e.preview || e.url} alt={e.name || e.file_name} />
                  }
                </div>
              ))}
            </div>
          )}
          {!fil.settings?.add_req_docs && fieldErrors.attachmentslist_c && showFormErrors && (
            <div className={styles.error}>
              <FormattedMessage id="pulpoforms.errors.not_blank" defaultMessage="This field cannot be empty" />
            </div>)
          }
        </>
      );
    }
    return null;
  };

  return (
  <>
    <div className={styles.header}>
      {'Personal Information'}
    </div>
    <div className={styles.fullInput}>
      <PulpoField
        key="first_name"
        id="first_name"
        type="string"
        translationID="noise_log.public.name"
        title="First Name"
        isRequired
        handleValueChange={(a, b) => handleAnswerChanged('info', a, b)}
        answer={info?.first_name}
        showFieldErrors={fieldErrors?.first_name && showFormErrors}
        handleFieldErrorChanged={(a, b) => handleFieldErrorChanged(a, b)}
        Badging
      />
      <PulpoField
        key="last_name"
        id="last_name"
        type="string"
        translationID="portal.user.last_name"
        title="Last Name"
        isRequired
        handleValueChange={(a, b) => handleAnswerChanged('info', a, b)}
        answer={info?.last_name}
        showFieldErrors={fieldErrors?.last_name && showFormErrors}
        handleFieldErrorChanged={(a, b) => handleFieldErrorChanged(a, b)}
        Badging
      />
      <PulpoField
        key="suffix"
        id="suffix"
        type="select"
        transationID="airfields.runway.suffix"
        title="Suffix"
        className={styles.fullInput1}
        handleValueChange={(a, b) => handleAnswerChanged('info', a, b)}
        answer={info.suffix}
        handleFieldErrorChanged={() => {}}
        values={[{ key: 'Jr', value: 'Jr' }, { key: 'Sr', value: 'Sr' }]}
        Badging
      />
    </div>
    {schemaSettings && schemaSettings?.personal_information?.length > 0 && (
      <div className={styles.notefont}>Note: {schemaSettings && schemaSettings?.personal_information}</div>
    )}
    <div className={styles.fullInput}>
      <PulpoField
        key="email"
        id="email"
        type="string"
        title="Email"
        translationID="forms.complete.email"
        handleValueChange={(a, b) => handleAnswerChanged('info', a, b)}
        answer={info?.email}
        isRequired
        className={styles.email}
        showFieldErrors={fieldErrors?.email && showFormErrors}
        handleFieldErrorChanged={(a, b) => handleFieldErrorChanged(a, b)}
        Badging
      />
      <PulpoField
        key="phone_number"
        id="phone_number"
        type="telephone"
        translationID="servicerequest.create.person_phone"
        title="Phone Number"
        isRequired
        handleValueChange={(a, b) => handleAnswerChanged('info', a, b)}
        answer={info?.phone_number}
        className={styles.phone}
        showFieldErrors={fieldErrors?.phone_number && showFormErrors}
        handleFieldErrorChanged={(a, b) => handleFieldErrorChanged(a, b)}
        Badging
      />
    </div>
    {hasCustomFields && orderedBlocks.map((block, index) => (
      <React.Fragment key={`${block.type}-${block.type === 'attachment' ? block.field?.id : index}`}>
        {block.type === 'custom' && (
          <CustomFields
            handleAnswerChanged={(a, b) => handleAnswerChanged('response', a, b)}
            handleFieldErrorChanged={handleFieldErrorChanged}
            showFormErrors={showFormErrors}
            requiredMap={requiredMap}
            fields={block.fields}
            answers={response}
            Badging
            forQuestionMarkHelp
            info={info}
            tenantName={tenantName}
            renewalApplicationData={renewalApplicationData}
          />
        )}
        {block.type === 'attachment' && renderAttachmentField(block.field)}
      </React.Fragment>
    ))}
    {pdfUrl !== undefined && (
      <div style={{ marginLeft: '490px' }}>
        <a href={`https://static.${pdfHost}/media/badging/demo_doc/${airportCode}.pdf`} target="_blank" rel="noopener noreferrer">Acceptable Documents for ID</a>
      </div>
    )}
    {applicantAcknowldgementMessage.length > 0 && (
      <Collapsible
        title="Applicant acknowledges their security responsibilities under 49 CFR
      1540.105(a)"
        styleClasses={styles.header}
        collapsibleHeight="0"
      >
        {applicantAcknowldgementMessage.length > 0 && (
          // eslint-disable-next-line react/no-danger
          <div dangerouslySetInnerHTML={{ __html: applicantAcknowldgementMessage }} />
        )}
      </Collapsible>
    )}
  </>
  );
};


Step0.propTypes = {
  info: PropTypes.shape({}).isRequired,
  handleAnswerChanged: PropTypes.func.isRequired,
  handleFieldErrorChanged: PropTypes.func.isRequired,
  showFormErrors: PropTypes.bool.isRequired,
  forQuestionMarkHelp: PropTypes.bool.isRequired
};

export default Step0;
